import React from 'react';
import {
  Badge,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  HStack,
  NumberInput,
  NumberInputField,
  Radio,
  Spacer,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@cardboard-ui/react';
import * as EmailValidator from 'email-validator';
import { Card, CardContent } from 'components/cards/Card';
import { graphql, useFragment } from 'utils/graphClient';
import { RespondQuestionCard_formQuestion$key } from './__generated__/RespondQuestionCard_formQuestion.graphql';
import { Input } from 'components/Form/Input';
import { t } from '@lingui/macro';
import { FormQuestionType } from 'components/icons/QuestionIcon';
import { CheckboxGroup } from 'components/Form/Checkbox';
import { AnswerInput } from '../__generated__/RespondForm_updateResponse_Mutation.graphql';
import { RadioGroup } from 'components/Form/Radio';
import { FieldOptionType } from 'components/FieldOption';
import { useI18n } from 'utils/i18n';
import { PhoneNumberInput } from 'components/PhoneNumberInput';

const fragment = graphql`
  fragment RespondQuestionCard_formQuestion on FormQuestion {
    id
    text
    required
    definition {
      __typename
      id
      ... on CustomField__Numeric {
        minValue
        maxValue
      }
      ... on CustomField__Checkboxes {
        options {
          value
          text
        }
      }
      ... on CustomField__MultipleChoice {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__Date {
        id
      }
      ... on CustomField__Time {
        id
      }
      ... on CustomField__DateTime {
        id
      }
      ... on CustomField__PhoneNumber {
        id
      }
      ... on CustomField__Link {
        id
      }
    }
  }
`;

interface RespondQuestionCardProps {
  form: any;
  question: RespondQuestionCard_formQuestion$key;
  saveAnswer: (newAnswer: AnswerInput) => void;
  isLoading: boolean;
}

export const RespondQuestionCard = ({
  form,
  question,
  saveAnswer,
  isLoading,
}: RespondQuestionCardProps) => {
  const {
    id: questionId,
    text,
    required,
    definition,
  } = useFragment(fragment, question);
  const { format } = useI18n();

  const inputValue = form.watch(questionId);
  const inputDateValue = form.watch(`${questionId}-date`);
  const inputTimeValue = form.watch(`${questionId}-time`);

  const {
    formState: { errors },
  } = form;
  const errorMessage =
    errors[questionId]?.message ||
    errors[`${questionId}-date`]?.message ||
    errors[`${questionId}-time`]?.message;

  return (
    <Card isNotClickable data-qa="respond-question-card">
      <CardContent>
        <Stack spacing={6}>
          <HStack p={2} pos="relative">
            <Text>{text}</Text>
            {required && <Badge>{t`Required`}</Badge>}
            <Spacer />
            <Spinner size="xs" hidden={!isLoading} pos="absolute" right={0} />
          </HStack>
        </Stack>

        {definition?.__typename === FormQuestionType.TEXT && (
          <Input
            type="text"
            value={inputValue || ''}
            errorMessage={errorMessage}
            {...form.register(questionId, {
              required: {
                value: required,
                message: t`Answer is required`,
              },
            })}
            onChange={(e) => {
              form.setValue(questionId, e.target.value || '');
              const stringValue = e.target.value
                ? {
                  value: e.target.value,
                }
                : null;
              saveAnswer({
                questionId,
                stringValue,
              });
            }}
          />
        )}

        {definition?.__typename === FormQuestionType.NUMERIC && (
          <FormControl isInvalid={!!errorMessage}>
            <NumberInput
              value={inputValue || ''}
              min={definition.minValue}
              max={definition.maxValue}
              keepWithinRange={false}
              errorMessage={errorMessage}
              clampValueOnBlur
              {...form.register(questionId, {
                required: {
                  value: required,
                  message: t`Answer is required`,
                },
              })}
              onChange={(_value) => {
                const value = parseInt(_value);
                form.setValue(questionId, value);

                if (isNaN(value)) {
                  return;
                }

                const clampedValue = Math.min(
                  definition.maxValue as number,
                  Math.max(definition.minValue as number, value),
                );
                const numericValue = value
                  ? {
                    value: clampedValue,
                  }
                  : null;
                saveAnswer({
                  questionId,
                  numericValue,
                });
              }}
            >
              <NumberInputField placeholder="123..." />
              {errorMessage ? <Box color="red.500">{errorMessage}</Box> : null}
              <FormHelperText>{t`A number between ${definition.minValue} and ${definition.maxValue}`}</FormHelperText>
            </NumberInput>
          </FormControl>
        )}

        {definition?.__typename === FormQuestionType.CHECKBOXES && (
          <CheckboxGroup
            errorMessage={errorMessage}
            value={inputValue || []}
            onChange={(newValue: string[]) => {
              form.setValue(questionId, newValue);
              const checkboxesValue = newValue?.length
                ? {
                  value: newValue,
                }
                : null;
              saveAnswer({
                questionId,
                checkboxesValue,
              });
            }}
          >
            <VStack p={2} spacing={4} align="start">
              {definition.options?.map((option: FieldOptionType) => (
                <Checkbox
                  key={option.value}
                  {...form.register(questionId, {
                    required: {
                      value: required,
                      message: t`Answer is required`,
                    },
                  })}
                  value={option.value}
                >
                  {option.text}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        )}

        {definition?.__typename === FormQuestionType.MULTIPLECHOICE && (
          <RadioGroup
            errorMessage={errorMessage}
            value={inputValue || ''}
            onChange={(newValue) => {
              form.setValue(questionId, newValue);
              const multipleChoiceValue = newValue
                ? {
                  value: newValue,
                }
                : null;
              saveAnswer({
                questionId,
                multipleChoiceValue,
              });
            }}
          >
            <VStack p={2} spacing={4} align="start">
              {definition.options?.map((option: FieldOptionType) => (
                <Radio
                  key={option.value}
                  {...form.register(questionId, {
                    required: {
                      value: required,
                      message: t`Answer is required`,
                    },
                  })}
                  value={option.value}
                >
                  {option.text}
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        )}

        {definition?.__typename === FormQuestionType.DATE && (
          <Input
            type="date"
            value={inputValue}
            errorMessage={errorMessage}
            {...form.register(questionId, {
              required: {
                value: required,
                message: t`Answer is required`,
              },
            })}
            onChange={(e) => {
              form.setValue(questionId, e.target.value || '');
              const dateValue = e.target.value
                ? {
                  value: e.target.value,
                }
                : null;
              saveAnswer({
                questionId,
                dateValue,
              });
            }}
          />
        )}

        {definition?.__typename === FormQuestionType.TIME && (
          <Input
            type="time"
            value={inputValue}
            errorMessage={errorMessage}
            {...form.register(questionId, {
              required: {
                value: required,
                message: t`Answer is required`,
              },
            })}
            onChange={(e) => {
              form.setValue(questionId, e.target.value || '');
              const timeValue = e.target.value
                ? {
                  value: e.target.value,
                }
                : null;
              saveAnswer({
                questionId,
                timeValue,
              });
            }}
          />
        )}

        {definition?.__typename === FormQuestionType.DATETIME && (
          <HStack>
            <Input
              type="date"
              value={inputDateValue}
              errorMessage={errorMessage}
              {...form.register(`${questionId}-date`, {
                required: {
                  value: required,
                  message: t`Answer is required`,
                },
              })}
              onChange={(e) => {
                const newDate = e.target.value;
                form.setValue(`${questionId}-date`, newDate);

                const existingTime = inputTimeValue
                  ? inputTimeValue
                  : format(new Date(newDate), 'HH:mm');
                form.setValue(
                  `${questionId}-time`,
                  newDate ? existingTime : '',
                );

                const dateTimeValue = newDate
                  ? {
                    value: new Date(
                      `${newDate}T${existingTime}`,
                    ).toISOString(),
                  }
                  : null;
                saveAnswer({
                  questionId,
                  dateTimeValue,
                });
              }}
            />
            <Input
              type="time"
              value={inputTimeValue}
              errorMessage={errorMessage}
              {...form.register(`${questionId}-time`, {
                required: {
                  value: required,
                  message: t`Answer is required`,
                },
              })}
              onChange={(e) => {
                const newTime = e.target.value;
                form.setValue(`${questionId}-time`, newTime);

                const existingDate = inputDateValue
                  ? inputDateValue
                  : format(new Date(), 'yyyy-MM-dd');
                form.setValue(`${questionId}-date`, existingDate);

                const dateTimeValue = {
                  value: new Date(`${existingDate}T${newTime}`).toISOString(),
                };
                saveAnswer({
                  questionId,
                  dateTimeValue,
                });
              }}
            />
          </HStack>
        )}

        {definition?.__typename === FormQuestionType.PHONE && (
          <PhoneNumberInput
            value={inputValue || ''}
            errorMessage={errorMessage}
            {...form.register(questionId, {
              required: {
                value: required,
                message: t`Answer is required`,
              },
            })}
            handleChange={(value) => {
              saveAnswer({
                questionId,
                phoneNumberValue: {
                  value,
                },
              });
            }}
          />
        )}

        {definition?.__typename === FormQuestionType.EMAIL && (
          <Input
            type="email"
            value={inputValue}
            errorMessage={errorMessage}
            {...form.register(questionId, {
              required: {
                value: required,
                message: t`Answer is required`,
              },
              validate: (value: string) => {
                if (!EmailValidator.validate(value)) {
                  return t`Answer is not a valid e-mail address`;
                }
              },
            })}
            onChange={(e) => {
              form.setValue(questionId, e.target.value || '');
              const emailValue =
                e.target.value && EmailValidator.validate(e.target.value)
                  ? {
                    value: e.target.value,
                  }
                  : null;
              saveAnswer({
                questionId,
                emailValue,
              });
            }}
          />
        )}

        {definition?.__typename === FormQuestionType.LINK && (
          <Input
            type="link"
            value={inputValue}
            errorMessage={errorMessage}
            {...form.register(questionId, {
              required: {
                value: required,
                message: t`Answer is required`,
              },
            })}
            onChange={(e) => {
              form.setValue(questionId, e.target.value || '');
              saveAnswer({
                questionId,
                linkValue: { value: e.target.value },
              });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};
